import React, { useEffect, useMemo } from 'react';

import { FormProvider } from 'react-hook-form';

import { ELoginMethod } from '@aqx-core/common/api/auth';
import { ECrmType } from '@aqx-core/common/api/user-account';
import { useLoginAsTabs } from '@aqx-core/common/hooks/auth/use-login-as-tab';
import { EInputType, ELoginStep, useOtpLoginState } from '@aqx-core/common/hooks/form/use-otp-login-state';
import { useAuthLoading, usePrefilledLoginDetails } from '@aqx-core/common/services/auth';
import { signUpDemoViewController } from '@aqx-core/common/services/sign-up-demo/controller';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';
import { useAppTheme } from '@aqx-core/common/theme';
import { useOtpLoginForm } from '@aqx-member/web/hooks/form/use-otp-login-form';

import OtpLoginStep1 from './otp-login-step1';
import OtpLoginStep2Otp from './otp-login-step2-otp';
import OtpLoginStep2Password from './otp-login-step2-password';

interface IOtpLoginFormProps {
  crmType?: ECrmType;
}

const useCountriesInit = () => {
  useEffect(() => {
    const onError = (message: string) => {
      snackbarController.showAlert({ message, messageType: EUISnackBarMessageType.ERROR });
    };
    signUpDemoViewController.initSignUpDemo(onError);
  }, []);
};

const OtpLoginForm: FCC<IOtpLoginFormProps> = ({ crmType = ECrmType.STONEINNO }) => {
  const { theme } = useAppTheme();
  const { tabs, selectedTab, onTabChange } = useLoginAsTabs();
  const prefilledDetails = usePrefilledLoginDetails();
  const { userId: prefilledUserId, password: prefilledPassword } = prefilledDetails || {};
  const { useFormMethods, createDynamicSubmit } = useOtpLoginForm();
  const authLoginLoading = useAuthLoading();

  useCountriesInit();

  const { watch, formState, register, reset, setValue, setFocus, trigger, clearErrors } = useFormMethods;
  const [userId, dialCode, phoneNumber, password] = watch(['userId', 'dialCode', 'phoneNumber', 'password']);
  const {
    errors: { password: passwordError },
    touchedFields
  } = formState;

  const otpState = useOtpLoginState({
    userId,
    dialCode,
    phoneNumber,
    reset,
    onTabChange,
    setValue,
    setFocus,
    clearErrors,
    crmType,
    prefilledUserId,
    prefilledPassword
  });

  const { currentStep, inputType, handleOtpError } = otpState;

  // Create submit functions with error handler for TW_1005
  const submitWithPassword = useMemo(() => createDynamicSubmit(ELoginMethod.PASSWORD), [createDynamicSubmit]);
  const submitWithEmailOtp = useMemo(
    () => createDynamicSubmit(ELoginMethod.EMAIL, handleOtpError),
    [createDynamicSubmit, handleOtpError]
  );
  const submitWithSmsOtp = useMemo(
    () => createDynamicSubmit(ELoginMethod.SMS, handleOtpError),
    [createDynamicSubmit, handleOtpError]
  );

  const isPasswordBtnDisabled = useMemo(() => !password || password.length === 0, [password]);
  const currentSubmitFunction = useMemo(() => {
    if (currentStep === ELoginStep.STEP_2_OTP) {
      return inputType === EInputType.EMAIL ? submitWithEmailOtp : submitWithSmsOtp;
    }
    return submitWithPassword;
  }, [currentStep, inputType, submitWithEmailOtp, submitWithSmsOtp, submitWithPassword]);

  const renderStep = () => {
    if (currentStep === ELoginStep.STEP_2_OTP) {
      return (
        <OtpLoginStep2Otp
          inputType={inputType}
          authLoginLoading={authLoginLoading}
          otpError={otpState.otpError}
          isOtpBtnDisabled={otpState.isOtpBtnDisabled}
          onOtpChange={otpState.handleOtpChange}
          onOtpValidate={otpState.handleOtpSubmit}
          onUsePassword={otpState.handleLoginWithPassword}
          onBack={otpState.handleBackToStep1}
          onFormSubmit={currentSubmitFunction}
          onResendOtp={otpState.handleResendOtp}
          remainingTimeText={otpState.remainingTimeText}
          isResendDisabled={otpState.isResendDisabled}
        />
      );
    }

    if (currentStep === ELoginStep.STEP_2_PASSWORD) {
      return (
        <OtpLoginStep2Password
          selectedTab={selectedTab}
          authLoginLoading={authLoginLoading}
          register={register}
          password={password}
          passwordError={passwordError?.message}
          handleKeyDown={otpState.handleKeyDown}
          isPasswordBtnDisabled={isPasswordBtnDisabled}
          onUseOtp={otpState.handleLoginWithOtp}
          onBack={otpState.handleBackToStep1}
          onFormSubmit={currentSubmitFunction}
        />
      );
    }

    return (
      <OtpLoginStep1
        tabs={tabs}
        selectedTab={selectedTab}
        onTabChange={otpState.handleTabChange}
        theme={theme}
        authLoginLoading={authLoginLoading}
        register={register}
        setValue={setValue}
        trigger={trigger}
        userId={userId}
        dialCode={dialCode}
        phoneNumber={phoneNumber}
        touchedFields={touchedFields}
        handleKeyDown={otpState.handleKeyDown}
        inputType={inputType}
        isStep1BtnDisabled={otpState.isStep1BtnDisabled}
        onOtpLogin={otpState.handleStep1OtpLogin}
        onPasswordLogin={otpState.handleStep1PasswordLogin}
        onFormSubmit={otpState.handleStep1Submit}
      />
    );
  };

  return <FormProvider {...useFormMethods}>{renderStep()}</FormProvider>;
};

export default OtpLoginForm;
